import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import styled from '@emotion/styled';
import type { StayCalendar, StaysListingHead, StaysPolicy } from '../../../../interfaces/stays';
import { getListingCalendar, reopenDates } from '../../../../api/stays-reservations-client';
import { MonthGrid } from '../../../shared/month-grid/month-grid';
import type { DayContext, DaySelection, FocusCause } from '../../../shared/month-grid/month-grid';
import { monthOf, monthTitle } from '../../../shared/month-grid/month-model';
import { useMediaQuery } from '../../../hooks/use-media-query';
import { AppButton } from '../../../button/app-button';
import { ClockNote } from '../../../clock-note/clock-note';
import { FormSkeleton, formSkeletonHeight, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { Card } from '../../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { Staleable, StateSwitch } from '../../../../motion';
import { addDays, formatDayKey } from '../../../../function-utils/building-time';
import { formatMoney } from '../../../../utils/stays-money';
import { useAuth } from '../../../../context/auth-provider';
import { isCohost } from '../../../../auth/roles';
import { up } from '../../../../style-utils/media-queries';
import { bp, layout, palette, radius, shadow, spacing, status, typography, zIndex } from '../../../../style-utils/theme';
import toast from '../../../../utils/toast';
import { buildingClock } from '../stays-reservations-model';
import { EditReservationModal } from '../reservation-form/edit-reservation-modal';
import { CalendarDay } from './calendar-day';
import { CalendarLegend } from './calendar-legend';
import { DaySheet } from './day-sheet';
import { ManageDatesSheet, ManageRange } from './manage-dates-sheet';
import { BlockManager } from './block-manager';
import { dayLabelOf, indexCalendar, mergeCalendars, nextMonthKey, orderRange, rolesOf } from './calendar-model';

/**
 * THE OPERATIONS CALENDAR (ai-plans/stays plan S4.10; K-68; R-S4.35).
 *
 * Six building months stacked (MonthGrid density="calendar"), "Show 6 more
 * months" appending the next window; four cards for the building's current
 * month; drag or click-click to close dates; the owner blocks card below.
 *
 * Every date is a BUILDING day key from the response (building_today,
 * check_in, end_date …) and every time the building's wall clock ('HH:MM' from
 * the server). Nothing here asks the device's clock; one ClockNote says whose
 * dates these are.
 *
 * The month list is unbounded (each "Show 6 more months" adds six), so it has
 * NO layout animation; a refetch dims it (Staleable) and never swaps it for a
 * skeleton.
 */

export interface StaysCalendarProps {
  listing: StaysListingHead;
  policy?: StaysPolicy;
  /** stays:manage — the drag-to-close, the owner-block card, the pills' edit modal. */
  canManage: boolean;
}

const WINDOW_MONTHS = 6;
/* Below md the cells are compact (marks only) and a tap opens the Day sheet. */
const PHONE_QUERY = `(max-width: ${bp.md - 0.02}px)`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
  /* The end of a scrolling dashboard page stays clear of the IQ launcher. */
  padding-bottom: ${layout.fabReserve};
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing.sm};
  min-width: 0;

  ${up('sm')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${up('lg')} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const SummaryCard = styled(Card)<{ $warn?: boolean }>`
  gap: ${spacing.half};
  border-color: ${({ $warn }) => ($warn ? palette.warning : palette.line)};
  background: ${({ $warn }) => ($warn ? status.warning.bg : palette.surface)};
`;

const SummaryLabel = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

const SummaryValue = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
`;

const SummarySub = styled.p<{ $warn?: boolean }>`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${({ $warn }) => ($warn ? typography.weight.semibold : typography.weight.regular)};
  color: ${({ $warn }) => ($warn ? status.warning.fg : palette.slate)};
  overflow-wrap: anywhere;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
`;

const Months = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.sm};

  ${up('md')} {
    padding: ${spacing.md};
  }
`;

const MonthSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
  scroll-margin-top: ${spacing.sm};
`;

const MonthHeading = styled.h2`
  position: sticky;
  top: 0;
  z-index: ${zIndex.sticky};
  margin: 0;
  padding: ${spacing.xs} 0;
  background: ${palette.surface};
  border-bottom: 1px solid ${palette.line};
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${palette.ink};
`;

const Selecting = styled.div`
  position: sticky;
  bottom: calc(${spacing.sm} + var(--safe-bottom));
  z-index: ${zIndex.sticky};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.teal};
  border-radius: ${radius.sm};
  background: ${palette.tealTint};
  box-shadow: ${shadow.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
`;

const InlineAlert = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.alertStrong};
`;

type View = 'loading' | 'error' | 'ready';

export const StaysCalendar: React.FC<StaysCalendarProps> = ({ listing, policy, canManage }) => {
  const desk = useDeskInPlan();
  // IS5: a co-host sees no amounts (the server strips revenue from their calendar).
  const hideMoney = isCohost(useAuth().user?.role);
  const uid = useId();
  const phone = useMediaQuery(PHONE_QUERY);

  /* ── the data: windows of six months, appended ── */
  const [windows, setWindows] = useState<StayCalendar[]>([]);
  const [view, setView] = useState<View>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [blocksKey, setBlocksKey] = useState(0);
  const request = useRef(0);
  const windowsRef = useRef<StayCalendar[]>([]);
  windowsRef.current = windows;

  const loadFirst = useCallback(async () => {
    const id = request.current + 1;
    request.current = id;
    setView('loading');
    try {
      const first = await getListingCalendar(listing.id, undefined, WINDOW_MONTHS);
      if (request.current !== id) return;
      setWindows([first]);
      setView('ready');
    } catch {
      if (request.current !== id) return;
      setView('error');
    }
  }, [listing.id]);

  useEffect(() => {
    setWindows([]);
    void loadFirst();
  }, [loadFirst]);

  /** Read every window on screen again (after a write). */
  const refetch = useCallback(async () => {
    const shown = windowsRef.current;
    if (!shown.length) {
      await loadFirst();

      return;
    }
    const id = request.current + 1;
    request.current = id;
    setRefreshing(true);
    setBlocksKey(k => k + 1);
    try {
      const next = await Promise.all(
        shown.map((w, i) =>
          i === 0
            ? getListingCalendar(listing.id, undefined, WINDOW_MONTHS)
            : getListingCalendar(listing.id, w.months.length ? w.months[0].key : undefined, WINDOW_MONTHS)
        )
      );
      if (request.current !== id) return;
      setWindows(next);
    } catch {
      if (request.current === id) toast.error("Couldn't refresh the calendar.");
    } finally {
      if (request.current === id) setRefreshing(false);
    }
  }, [listing.id, loadFirst]);

  const data = useMemo(() => mergeCalendars(windows), [windows]);
  const index = useMemo(() => (data ? indexCalendar(data) : null), [data]);
  const monthKeys = useMemo(() => (data ? data.months.map(m => m.key) : []), [data]);

  const showMore = async () => {
    if (!monthKeys.length || moreLoading) return;
    const from = nextMonthKey(monthKeys[monthKeys.length - 1]);
    const id = request.current;
    setMoreLoading(true);
    setMoreError(false);
    try {
      const more = await getListingCalendar(listing.id, from, WINDOW_MONTHS);
      if (request.current !== id) return;
      setWindows(prev => [...prev, more]);
    } catch {
      setMoreError(true);
    } finally {
      setMoreLoading(false);
    }
  };

  /* ── the cursor, the selection, the sheets ── */
  const today = data ? data.building_today : '';
  const [focusDay, setFocusDay] = useState<string>('');
  const cursor = focusDay || today;
  const [selection, setSelection] = useState<DaySelection | null>(null);
  const [anchor, setAnchor] = useState<string | null>(null);
  const anchorRef = useRef<string | null>(null);
  const [manageRange, setManageRange] = useState<ManageRange | null>(null);
  const [daySheet, setDaySheet] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  /* Which key activated a day: Enter selects, Space opens the Day sheet. */
  const activationKey = useRef<string | null>(null);

  const setAnchorBoth = (day: string | null) => {
    anchorRef.current = day;
    setAnchor(day);
  };

  const startSelection = (day: string) => {
    setAnchorBoth(day);
    setSelection({ start: day, end: day });
  };

  const extendSelection = (day: string) => {
    const from = anchorRef.current;
    if (from) setSelection({ start: from, end: day });
  };

  const finishSelection = (day: string) => {
    const range = orderRange(anchorRef.current || day, day);
    setAnchorBoth(null);
    setSelection(range);
    setManageRange(range);
  };

  const cancelSelection = () => {
    setAnchorBoth(null);
    setSelection(null);
  };

  const onDayActivate = (day: string) => {
    const key = activationKey.current;
    activationKey.current = null;
    if (canManage && anchorRef.current) {
      finishSelection(day);

      return;
    }
    if (phone || !canManage || key === ' ') {
      setDaySheet(day);

      return;
    }
    startSelection(day);
  };

  const onFocusDayChange = (day: string, cause: FocusCause) => {
    if (!monthKeys.includes(monthOf(day))) return;
    setFocusDay(day);
    if (canManage && anchorRef.current && cause !== 'pointer' && cause !== 'focus') extendSelection(day);
  };

  const openStay = (id: string) => {
    setDaySheet(null);
    setEditId(id);
  };

  const reopenDay = async (day: string) => {
    try {
      await reopenDates(listing.id, { start_date: day, end_date: addDays(day, 1) });
      toast.success('Date re-opened.');
      setDaySheet(null);
      void refetch();
    } catch (e) {
      toast.error(e instanceof Error && e.message ? e.message : 'Could not re-open this date.');
    }
  };

  const closeManage = () => {
    setManageRange(null);
    setSelection(null);
  };

  const headingId = (key: string) => `${uid}-month-${key}`;
  const sectionId = (key: string) => `${uid}-section-${key}`;

  const scrollToToday = () => {
    if (!today) return;
    setFocusDay(today);
    const node = document.getElementById(sectionId(monthOf(today)));
    if (node && typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'start' });
  };

  /* The day labels are the slowest thing a render does (one Intl call per
     day), so they are worked out once per calendar: a fresh cache for every
     new calendar, filled as the grids ask. */
  const labels = useMemo(() => new Map<string, string>(), [data, index]);
  const dayLabel = (day: string): string => {
    if (!data || !index) return day;
    const known = labels.get(day);
    if (known) return known;
    const label = dayLabelOf(day, rolesOf(index, day), index, data);
    labels.set(day, label);

    return label;
  };

  const renderDay = (day: string, ctx: DayContext) =>
    data && index ? (
      <CalendarDay
        day={day}
        ctx={ctx}
        roles={rolesOf(index, day)}
        index={index}
        data={data}
        compact={phone}
        canManage={canManage}
        onOpenStay={openStay}
      />
    ) : null;

  const currentMonth = today ? monthOf(today) : '';
  const summary = data ? (data.months.find(m => m.key === currentMonth) || null) : null;
  const currency = (data && data.currency) || listing.currency;
  const s = summary ? summary.summary : null;
  const gapsWarn = !!s && s.one_night_gaps > 0;

  return (
    <StateSwitch state={view} minHeight={formSkeletonHeight(4)}>
      {view === 'loading' ? (
        <FormSkeleton fields={4} label="the calendar" />
      ) : view === 'error' || !data || !index ? (
        <LoadError message="Couldn't load the calendar" onRetry={() => void loadFirst()} />
      ) : (
        <Staleable stale={refreshing}>
          <Root data-stays-calendar={listing.id}>
            <ClockNote clock={buildingClock(data.building_zone || listing.timezone)} suffix={`· every date is the ${w('building', desk)}'s`} />

            <Summary aria-label={currentMonth ? `${monthTitle(currentMonth)} at a glance` : undefined}>
              <SummaryCard data-summary="occupied" aria-labelledby={`${uid}-s-occ`}>
                <SummaryLabel id={`${uid}-s-occ`}>Occupied nights</SummaryLabel>
                <SummaryValue>{s ? s.occupied_nights : 0}</SummaryValue>
              </SummaryCard>
              {hideMoney ? null : (
                <SummaryCard data-summary="revenue" aria-labelledby={`${uid}-s-rev`}>
                  <SummaryLabel id={`${uid}-s-rev`}>Booked revenue</SummaryLabel>
                  <SummaryValue>{formatMoney(s ? s.revenue_cents : 0, currency)}</SummaryValue>
                  <SummarySub>Allocated by night</SummarySub>
                </SummaryCard>
              )}
              <SummaryCard data-summary="gaps" data-tone={gapsWarn ? 'warning' : undefined} $warn={gapsWarn} aria-labelledby={`${uid}-s-gap`}>
                <SummaryLabel id={`${uid}-s-gap`}>One-night gaps</SummaryLabel>
                <SummaryValue>{s ? s.one_night_gaps : 0}</SummaryValue>
                {gapsWarn ? <SummarySub $warn>Hard to sell — review pricing</SummarySub> : null}
              </SummaryCard>
              <SummaryCard data-summary="blocks" aria-labelledby={`${uid}-s-blk`}>
                <SummaryLabel id={`${uid}-s-blk`}>Owner blocks</SummaryLabel>
                <SummaryValue>{s ? s.owner_blocks : 0}</SummaryValue>
              </SummaryCard>
            </Summary>

            <Toolbar>
              <AppButton variant="secondary" onClick={scrollToToday}>
                Today
              </AppButton>
              <AppButton variant="secondary" loading={moreLoading} loadingLabel="Loading more months" onClick={() => void showMore()}>
                Show 6 more months
              </AppButton>
              {moreError ? <InlineAlert role="alert">Couldn&apos;t load more months. Try again.</InlineAlert> : null}
            </Toolbar>

            <CalendarLegend />

            <Months
              data-months={monthKeys.join(',')}
              onKeyDownCapture={event => {
                if (event.key === ' ' || event.key === 'Enter') activationKey.current = event.key;
              }}
              onPointerDownCapture={() => {
                activationKey.current = null;
              }}
            >
              {monthKeys.map(key => (
                <MonthSection key={key} id={sectionId(key)} aria-labelledby={headingId(key)} data-month-section={key}>
                  <MonthHeading id={headingId(key)}>{monthTitle(key)}</MonthHeading>
                  <MonthGrid
                    month={key}
                    today={data.building_today}
                    density="calendar"
                    headingId={headingId(key)}
                    headingLevel={2}
                    renderDay={renderDay}
                    dayLabel={dayLabel}
                    selectable={canManage}
                    selection={canManage ? selection : null}
                    onDayActivate={onDayActivate}
                    onSelectionStart={canManage ? startSelection : undefined}
                    onSelectionExtend={canManage ? extendSelection : undefined}
                    onSelectionEnd={canManage ? finishSelection : undefined}
                    focusDay={cursor}
                    onFocusDayChange={onFocusDayChange}
                    onEscape={canManage ? cancelSelection : undefined}
                    inTabOrder={monthOf(cursor) === key}
                  />
                </MonthSection>
              ))}
              {canManage && anchor ? (
                <Selecting role="status" data-selecting={anchor}>
                  <span>{`Closing from ${formatDayKey(anchor)} — choose the last night.`}</span>
                  <AppButton variant="ghost" onClick={cancelSelection}>
                    Cancel
                  </AppButton>
                </Selecting>
              ) : null}
            </Months>

            {canManage ? (
              <BlockManager listingId={listing.id} today={data.building_today} reloadKey={blocksKey} onChanged={() => void refetch()} />
            ) : null}

            <DaySheet
              day={daySheet}
              data={data}
              index={index}
              canManage={canManage}
              onClose={() => setDaySheet(null)}
              onOpenStay={openStay}
              onCloseFrom={day => {
                setDaySheet(null);
                startSelection(day);
              }}
              onReopenDay={reopenDay}
            />

            {canManage ? (
              <ManageDatesSheet
                range={manageRange}
                listingId={listing.id}
                data={data}
                onClose={closeManage}
                onDone={() => {
                  closeManage();
                  void refetch();
                }}
              />
            ) : null}

            {canManage ? (
              <EditReservationModal
                reservationId={editId}
                listing={listing}
                policy={policy}
                focus={null}
                onClose={() => setEditId(null)}
                onSaved={() => void refetch()}
              />
            ) : null}
          </Root>
        </Staleable>
      )}
    </StateSwitch>
  );
};
